import React, { useEffect, useState, useRef, useMemo } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { autocompleteTheme, buttonClasses, DateTimePickerTheme } from "../utils/theme";
import PreviewFiles, { FileView } from "../utils/PreviewFiles";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";

import {
	Typography,
	TextField,
	FormControl,
	FormLabel,
	MenuItem,
	FormControlLabel,
	Checkbox,
	RadioGroup,
	Radio,
	FormGroup,
	Grid,
	Autocomplete,
	Tooltip,
	InputAdornment,
	IconButton,
	TableContainer,
	Paper,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Box,
} from "@mui/material";

import theme, { inputClasses, textClasses } from "../utils/theme";
import { base64ToFile, compareObj, findDeepValue, getShortText } from "../../services/functions";
import { AddIcon, CloseIcon, CheckIcon, PreviewIcon, EyeIcon, EyeCloseIcon } from "../../services/icons";
import { Button, Input, Loader } from "../utils";
import dayjs from "dayjs";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { inputMaskOperations } from "./helper";

export const CustomInput = ({
	component,
	index = null,
	autoFillOnFocus,
	errors,
	fetchSourceDropdownData,
	onDropdownChange,
	onInputChange,
	onQuickAddSave,
	handleFormSubmit,
	onClose,
	btnText,
	isSubmitDisabled,
	isSaveDraft,
	onFetchBtnClick,
	onTableRowSelect,
	loadingFields,
	filterOnly = false,
}) => {
	const inputRefs = useRef([]);
	const inputFileRef = useRef();
	const [focusField, setFocusField] = useState(0);
	useEffect(() => {
		const { dynamic, dontFetch, api, apiMethod, values } = component;
		if (!dontFetch && dynamic && api && apiMethod && !values?.length) {
			fetchSourceDropdownData(component);
		}
	}, []);
	useEffect(() => {
		if (component.type === "file" && Array.isArray(component.value)) {
			const fileArray = base64ToFile(component?.value);
			const dataTransfer = new DataTransfer();
			fileArray.forEach((file) => dataTransfer.items.add(file));
			if (inputFileRef.current) {
				inputFileRef.current.files = dataTransfer.files;
			}
		}
	}, [component.value]);

	const [isQuickAddActive, setIsQuickAddActive] = useState(false);
	const [quickAddValue, setQuickAddValue] = useState("");
	const [showPreview, setShowPreview] = useState(true);
	const handleQuickAddActive = () => {
		setIsQuickAddActive(true);
	};
	const handleQuickAddCancel = () => {
		setIsQuickAddActive(false);
		setQuickAddValue("");
	};
	const handleQuickAddChange = (e) => {
		let { name, value } = e.target;
		if (component.inputMask && inputMaskOperations[component.inputMask]) {
			value = inputMaskOperations[component.inputMask](value);
		}
		setQuickAddValue(value);
	};
	const handleQuickAddSubmit = () => {
		onQuickAddSave(component, quickAddValue, handleQuickAddCancel);
	};

	const convertDocsBase64 = (file) => {
		if (file) {
			return new Promise((resolve, reject) => {
				setTimeout(() => {
					const fileReader = new FileReader();
					fileReader.readAsDataURL(file);
					fileReader.onload = () => {
						resolve(fileReader.result);
					};
					fileReader.onerror = (error) => {
						reject(error);
					};
				}, 400);
			});
		} else {
		}
	};

	let basicProps = {
		size: "small",
		fullWidth: true,
		type: component.type,
		multiline: component.type == "textarea",
		rows: component.type == "textarea" ? 3 : 1,
		label: `${component.label}${component.required ? " *" : ""}`,
		placeholder: component.placeholder,
		onFocus: autoFillOnFocus ? autoFillOnFocus : () => { },
		error: errors && errors[component._id] ? true : false,
		InputLabelProps: { shrink: true },
	};

	if (component.type == "number") {
		basicProps.onWheel = (event) => event.target.blur();
		basicProps.onKeyDown = (e) => {
			if (["e", "E", "+", "-"].includes(e.key)) {
				e.preventDefault();
			} else if (e.key == "0" && (e.target.value == "" || e.target.value[0] == "0")) {
				e.preventDefault();
			}
		};
		basicProps = {
			...basicProps,
		};
	}
	if (component.type == "date") {
		basicProps.InputLabelProps = { shrink: true };
		basicProps = {
			...basicProps,
		};
	}
	if (component.type == "file") {
		basicProps.focused = true;
		basicProps.onChange = async (e) => {
			const file = await convertDocsBase64(e.target.files[0]);
		};
	}

	if (component.type != "file") {
		basicProps = {
			...basicProps,
		};
	}

	let select_value = null;
	let select_options = [];
	select_options = getFieldOptions(component);
	select_value = useMemo(() => {
		if (component?.multiselect) {
			return select_options?.filter((chk) => [...component.value]?.find((fchk) => fchk == chk.value));
		} else {
			return select_options?.find((dl) => dl.value == (index != null && Array.isArray(component.value) ? component.value[index] || "" : component.value || "")) || null;
		}
	}, [component?.value, select_options]);

	// console.log({ [component._id]: loadingFields[component._id] });

	if (loadingFields[component._id]) {
		//&& component.type == "fetch"
		return <Loader size="1rem" height="2rem" isCircle />;
	}
	const handleShowHidePreview = () => {
		if (!component?.value || component.value.length === 0) {
			setShowPreview(false);
		} else {
			setShowPreview((prev) => !prev);
		}
	};
	if (["input", "number", "textarea", "file", "searchTags", "text", "qr", "rfid"].indexOf(component.type) >= 0) {
		if (component.multiple && Array.isArray(component.value)) {
			const changeParent = (value) => {
				onInputChange({ target: { value } });
			};
			const handleAdd = (index) => {
				changeParent([...component.value, ""]);
				setFocusField(index + 1);
			};

			const handleFocus = (index) => {
				setFocusField(index);
			};
			const handleRemove = (index) => {
				const newVal = [...component.value];
				newVal.splice(index, 1);
				changeParent(newVal);
				inputRefs.current.splice(index, 1);
				setFocusField(newVal.length - 1);
			};
			const handleChange = (e, i) => {
				const newVal = [...component.value];
				newVal.splice(i, 1, e.target.value);
				changeParent(newVal);
			};
			return (
				<FormControl variant="outlined" sx={{ width: "100%", marginTop: "-15px" }}>
					<Typography sx={{ ...textClasses.boldText, fontSize: "12px", lineHeight: "15px" }}>{`${component.label}${component.required ? " *" : ""}`}</Typography>

					{component.value?.map((val, i) => {
						return (
							<TextField
								autoFocus={i == 0}
								key={i}
								size="small"
								variant="outlined"
								inputRef={(ref) => (inputRefs.current[i] = ref)}
								sx={{ ...inputClasses.textField, marginBottom: "0.5rem" }}
								onFocus={() => handleFocus(i)}
								InputProps={{
									sx: { ...inputClasses.textField },
									readOnly: component?.readonly || false,
									endAdornment: (
										<InputAdornment position="end">
											{component.value.length > 1 ? (
												<IconButton aria-label="remove item" onClick={() => handleRemove(i)} edge="end">
													<CloseIcon style={{ fill: "var(--icon-color)" }} />
												</IconButton>
											) : null}
											{i == component.value.length - 1 ? (
												<IconButton aria-label="add item" onClick={() => handleAdd(i)} edge="end">
													<AddIcon style={{ fill: "var(--icon-color)" }} />
												</IconButton>
											) : null}
										</InputAdornment>
									),
								}}
								type={component.type}
								value={val || ""}
								onChange={(e) => {
									handleChange(e, i);
								}}
								onKeyDown={(e) => (e.key == "Enter" || e.keyCode == "13" ? handleAdd(i) : "")}
								label=""
								placeholder=""
							/>
						);
					})}
				</FormControl>
			);
		} else {
			let val =
				component.type != "file"
					? index != null && Array.isArray(component.value)
						? component.value[index] == undefined
							? ""
							: component.value[index]
						: component.value == undefined
							? ""
							: component.value
					: null;
			if (component.type == "date") {
				// console.log({ val, i: component._id });
				val = dayjs(val).format("YYYY-MM-DD");
				// console.log(val);
			}
			return (
				<>
					<TextField
						{...basicProps}
						onChange={onInputChange}
						value={val}
						sx={{ ...inputClasses.textField }}
						InputProps={{
							sx: { ...inputClasses.textField },
							readOnly: component?.readonly || false,
							endAdornment: (
								<InputAdornment position="end">
									{component.type === "file" && component?.value ? (
										<IconButton aria-label="remove item" edge="end" onClick={handleShowHidePreview} title={showPreview ? "Hide Preview" : "Show Preview"}>
											{showPreview ? <EyeCloseIcon style={{ fill: "var(--icon-color)" }} /> : <EyeIcon style={{ fill: "var(--icon-color)" }} />}
										</IconButton>
									) : null}
								</InputAdornment>
							),
							inputProps: {
								...(component?.multiselect == true ? { multiple: true } : {}),
							},
						}}
						inputRef={(ref) => (inputFileRef.current = ref)}
					/>

					{component.type == "file" && component.value?.length && showPreview ? (
						<PreviewFiles
							files={component.value}
							onClose={(fileIndexToRemove) => {
								if (Array.isArray(component.value)) {
									const updatedFiles = component.value.filter((_, i) => i !== fileIndexToRemove);
									const fileArray = base64ToFile(updatedFiles);
									const dataTransfer = new DataTransfer();
									fileArray.forEach((file) => dataTransfer.items.add(file));
									onInputChange({
										component,
										target: {
											files: fileArray,
										},
										index,
										fileClose: true,
									});

									if (inputFileRef.current) {
										inputFileRef.current.files = dataTransfer.files;
										if (updatedFiles.length === 0) {
											inputFileRef.current.value = "";
											setShowPreview(false);
										}
									}
								}
							}}
						/>
					) : null}
				</>
			);
		}
	} else if (component.type == "camera" || component.type == "image") {
		return component.value && showPreview ? (
			<FileView file={component.value} />
		) : null
	} else if (component.type == "date") {
		return (
			<ThemeProvider theme={DateTimePickerTheme}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<MuiDatePicker
						className="customDatePicker"
						sx={{ ...inputClasses.shadowField, fontSize: "8px", width: "100%" }}
						slotProps={{ textField: { size: "small" } }}
						format={"DD/MM/YYYY"}
						name={component._id}
						value={component.value == "#currentDate" ? dayjs(new Date()) : dayjs(component.value)?.$d == "Invalid Date" ? null : dayjs(component.value)}
						onChange={(v) => onInputChange({ target: { value: v.toISOString() } })}
						label={component.label}
						disabled={component.disabled}
						// minDate={minDATE}
						// maxDate={maxDATE}
						isRequired={component.required}
					/>
				</LocalizationProvider>
			</ThemeProvider>
		);
	} else if (component.type == "select") {
		const handleDropdownChange = (event, selectedOption, reason, details) => {
			if (component?.multiselect) {
				// this is to restrict how many options can be selected at a time
				let _val = selectedOption.map((ele) => ele.value);
				if (component.restrictMultiselect && _val.length > component.restrictMultiselectCount) {
					return;
				}
				onDropdownChange(_val);
			} else {
				onDropdownChange(selectedOption?.value == undefined ? "" : selectedOption.value);
			}
		};

		return (
			<div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "0.5rem", alignItems: "center" }}>
				{isQuickAddActive ? (
					<Input label={component.label} name="quickAddValue" value={quickAddValue} onChange={handleQuickAddChange} />
				) : (
					<FormControl fullWidth={basicProps.fullWidth} size={basicProps.size} error={basicProps.error}>
						<ThemeProvider theme={autocompleteTheme}>
							<Autocomplete
								id={component._id}
								sx={{ ...inputClasses.themeBoundary }}
								onFocus={basicProps.onFocus}
								options={select_options}
								multiple={component?.multiselect}
								autoComplete
								size="small"
								includeInputInList
								filterSelectedOptions
								noOptionsText={component.isSearching ? "Searching..." : "No Data Found"}
								value={select_value}
								onChange={handleDropdownChange}
								isOptionEqualToValue={(option, value) => {
									return (option?.value == "" && value == "") || option?.value == value?.value;
								}}
								onInputChange={(e, newInputValue, reason) => {
									//e.type = "click" when option is selected
									if (e?.type == "change" || reason == "clear") {
										onInputChange({ target: { value: newInputValue } });
									}
								}}
								renderInput={(params) => {
									return (
										<TextField
											{...params}
											{...basicProps}
											label={`${component.label}${component.required ? " *" : ""}`}
											fullWidth
											sx={{ ...inputClasses.themeBoundary, ...inputClasses.textField }}
										/>
									);
								}}
								renderOption={({ key, ...props }, option, { selected }) => {
									return (
										<MenuItem value={option.value} sx={{ justifyContent: "space-between", color: "var(--text-color)" }} key={key + props["data-option-index"]} {...props}>
											<Tooltip sx={{ width: "50px" }} title={option.label}>
												<Typography>{getShortText(option.label, 21)}</Typography>
											</Tooltip>
											{selected ? <CheckIcon color="info" /> : null}
										</MenuItem>
									);
								}}
								readOnly={component?.readonly}
							/>
						</ThemeProvider>
					</FormControl>
				)}
				{component.quickAdd && !filterOnly ? (
					isQuickAddActive ? (
						<>
							<Button text="Save" style={{ ...buttonClasses.lynkitOrangeFill }} onClick={handleQuickAddSubmit} />
							<Button text="Cancel" style={{ ...buttonClasses.lynkitBlackFill }} onClick={handleQuickAddCancel} />
						</>
					) : (
						<Button text="Add" style={{ ...buttonClasses.lynkitGreenFill }} onClick={handleQuickAddActive} disabled={!!component.value} />
					)
				) : null}
			</div>
		);
	} else if (component.type == "radio") {
		const innerComp = (
			<RadioGroup row sx={{ mt: 1 }} value={index != null && Array.isArray(component.value) ? component.value[index] || "" : component.value || ""} onChange={onInputChange}>
				{component.values &&
					component.values.map((op, i) => {
						return (
							<FormControlLabel
								key={i}
								label={<Typography sx={{ fontSize: "13px", color: "var(--text-color)" }}>{op.label}</Typography>}
								control={<Radio value={op.value} size="small" sx={{ "&.Mui-checked": { color: theme.themeOrange } }} />}
							/>
						);
					})}
			</RadioGroup>
		);
		if (component.multiline != null && component.multiline == false) {
			return (
				<Grid container justifyContent={"left"} alignItems="center">
					{innerComp}
				</Grid>
			);
		}
		return (
			<div style={{ ...inputClasses.shadowField, border: `1px solid ${basicProps.error ? "#e74c3c" : "#D9D9D9"}`, width: "100%", padding: "10px" }}>
				<FormControl fullWidth size="small">
					<FormLabel
						sx={{
							color: "var(--text-color)",
							"&.Mui-focused": {
								color: "var(--text-color)",
							},
						}}
					>
						{component.label}
					</FormLabel>
					{innerComp}
				</FormControl>
			</div>
		);
	} else if (component.type == "checkbox") {
		const innerComp = (
			<FormGroup row sx={{ mt: 1 }}>
				{component.values &&
					component.values.map((op, i) => {
						return (
							<FormControlLabel
								key={i}
								label={<Typography sx={{ fontSize: "13px", color: "var(--text-color)" }}>{op.label}</Typography>}
								control={
									<Checkbox
										checked={index != null && Array.isArray(component.value) ? component.value[index] || "" : component.value == op.value}
										onClick={() => onInputChange({ target: { value: op.value } })}
										size="small"
										sx={{ "&, &.Mui-checked": { color: theme.themeOrange } }}
									/>
								}
							/>
						);
					})}
			</FormGroup>
		);
		if (component.multiline != null && component.multiline == false) {
			return (
				<Grid container justifyContent={"left"} alignItems="center">
					{innerComp}
				</Grid>
			);
		}
		return (
			<div style={{ ...inputClasses.shadowField, border: "1px solid #D9D9D9", width: "100%", padding: "10px" }}>
				<FormControl fullWidth size="small">
					<FormLabel
						sx={{
							color: "var(--text-color)",
							"&.Mui-focused": {
								color: "var(--text-color)",
							},
						}}
					>
						{component.label}
					</FormLabel>
					{innerComp}
				</FormControl>
			</div>
		);
	} else if (component.type == "submit") {
		return (
			<div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem", marginTop: "1rem" }}>
				<Button
					onClick={
						isSaveDraft
							? () => {
								handleFormSubmit("draft");
							}
							: onClose
					}
					text={btnText}
					style={{ ...buttonClasses.lynkitOrangeEmpty, width: "fit-content" }}
				/>
				<Button type="submit" text={component.label} variant="text" style={{ ...buttonClasses.lynkitOrangeFill, width: "fit-content" }} disabled={isSubmitDisabled} />
			</div>
		);
	} else if (component.type == "fetch") {
		return (
			<div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem", marginTop: "1rem" }}>
				<Button
					type="button"
					text={component.label}
					variant="text"
					style={{ ...buttonClasses.lynkitOrangeFill, width: "fit-content" }}
					onClick={() => onFetchBtnClick(component)}
					disabled={component.disabled}
				/>
			</div>
		);
	} else if (component.type == "table") {
		const headers = component.isCustomHeader ? component.tableHeaders.map((_) => _.label) : Object.keys(component.values[0] || {});
		const columns = headers.map((h) => ({ field: h, headerName: h, flex: 1, minWidth: 200 }));
		let rows = [];
		if (component.isCustomHeader && component.tableHeaders?.length) {
			component.values.map((row, i) => {
				let _row = component.tableHeaders.reduce((res, curr) => {
					res[curr.label] = findDeepValue(row, curr.value.split("."));
					res.id = i;
					return res;
				}, {});
				rows.push(_row);
			});
		} else {
			component.values.map((row, i) => {
				let _row = {
					id: i,
				};
				Object.keys(row).map((key) => {
					_row[key] = typeof row[key] == "string" ? row[key] || "N/A" : "N/A";
				});
				rows.push(_row);
			});
		}
		// console.log(columns, rows);
		if (loadingFields[component._id]) return <Loader size="1rem" height="2rem" />;
		if (!component.values.length) return null;
		return (
			<Box sx={{ maxHeight: "20rem", width: "100%" }}>
				<DataGrid
					// disableColumnResize
					rows={rows}
					columns={columns}
					// pageSizeOptions={[5]}
					checkboxSelection={component.selectableTable}
					disableMultipleRowSelection={!component.multiselect}
					// disableRowSelectionOnClick
					getRowClassName={(params) => "tableRow"}
					sx={{
						"& .MuiDataGrid-checkboxInput": {
							color: theme.themeOrange, // Change checkbox color
							"&.Mui-checked": {
								color: theme.themeOrange, // Change checked checkbox color
							},
						},
						"& .Mui-selected": {
							backgroundColor: `rgba(255, 114, 0, 0.5) !important;`,
						},
						"& .MuiDataGrid-cell:focus , .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus-within": {
							outline: "unset;",
						},
						"& .MuiDataGrid-columnHeader": {
							// backgroundColor: "rgba(94,94,94, 0.5) !important"
						},
					}}
					onRowSelectionModelChange={(selectedIndices) => {
						const selectedRows = component.values
							.filter((_, i) => selectedIndices.includes(i))
							.map((obj) => {
								let _obj = {};
								for (let k in obj) {
									if (obj[k] !== "") _obj[k] = obj[k];
								}
								return _obj;
							});

						// console.log({ selectedRows }, selectedIndices);
						onTableRowSelect(component, selectedRows);
					}}
				/>
			</Box>
		);
		// return component.values?.length ? (
		// 	<TableContainer component={Paper} sx={{ maxHeight: "20rem" }}>
		// 		<Table aria-label="dynamic table">
		// 			<TableHead>
		// 				<TableRow>
		// 					<TableCell align="center">
		// 						{component.selectableTable && component.multiselect ? (
		// 							<Checkbox
		// 								checked={Array.isArray(component.value) ? component.value?.length == component.values?.length : false}
		// 								onChange={() => {
		// 									onTableRowSelect(component);
		// 								}}
		// 								name={`tableRow`}
		// 								sx={{ "&, &.Mui-checked": { color: theme.themeOrange } }}
		// 							/>
		// 						) : (
		// 							""
		// 						)}
		// 					</TableCell>

		// 					{headers.map((header, i) => {
		// 						return (
		// 							<TableCell align="center" key={i} sx={{ fontWeight: "bold" }}>
		// 								{header}
		// 							</TableCell>
		// 						);
		// 					})}
		// 				</TableRow>
		// 			</TableHead>

		// 			<TableBody>
		// 				{component.values
		// 					? component.values.map((row, i) => {
		// 							let checked = false;

		// 							if (Array.isArray(component.value) && component.value.length) {
		// 								checked = !!component.value.find((v) => compareObj(v, row));
		// 							} else {
		// 								checked = compareObj(component.value, row);
		// 							}

		// 							return (
		// 								<TableRow
		// 									key={i}
		// 									sx={{ "&:last-child td, &:last-child th": { border: 0 }, display: !component.multiselect && Object.keys(component.value).length && !checked ? "none" : "" }}
		// 								>
		// 									<TableCell align="center">
		// 										<div style={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
		// 											{component.selectableTable ? (
		// 												<Checkbox
		// 													// checked={Array.isArray(component.value) ? component.value.includes(i) : false}
		// 													checked={checked}
		// 													onChange={() => {
		// 														// onTableRowSelect(component, i);
		// 														onTableRowSelect(component, row);
		// 													}}
		// 													name={`tableRow-${i}`}
		// 													sx={{ "&, &.Mui-checked": { color: theme.themeOrange } }}
		// 												/>
		// 											) : (
		// 												i + 1
		// 											)}
		// 										</div>
		// 									</TableCell>
		// 									{component.isCustomHeader
		// 										? component.tableHeaders?.map((header, j) => {
		// 												return (
		// 													<TableCell align="center" key={j}>
		// 														{findDeepValue(row, header.value.split(".")) || "NA"}
		// 													</TableCell>
		// 												);
		// 										  })
		// 										: Object.keys(row).map((key, j) => {
		// 												return (
		// 													<TableCell align="center" key={j}>
		// 														{typeof row[key] == "string" ? row[key] || "N/A" : "N/A"}
		// 													</TableCell>
		// 												);
		// 										  })}
		// 								</TableRow>
		// 							);
		// 					  })
		// 					: null}
		// 			</TableBody>
		// 		</Table>
		// 	</TableContainer>
		// ) : null;
	}
};

function getFieldOptions(field) {
	const { values, apiBindingKey, apiBindingValue, dynamic } = field;
	const options = values?.map((el) => {
		let label = el[apiBindingKey || "label"];
		if (label == undefined) label = "";
		let value = el[apiBindingValue || "value"];
		if (value == undefined) value = "";
		return { label, value, tripId: el.trip_counter };
	});
	return options;
}
